import api from '../shared/services/api';

export interface Finding {
  code: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  field?: string;
  expected?: string;
  actual?: string;
  suggested_fix?: string;
  fix_confidence?: number;
  fix_source?: string;
}

export interface AreaResult {
  area: string;
  score: number;
  status: 'pass' | 'warning' | 'fail' | 'error';
  findings: Finding[];
  recommendations: string[];
}

export interface AuditReport {
  id?: string;
  overall_risk_score: number;
  overall_status: 'compliant' | 'requires_review' | 'non_compliant';
  area_results: AreaResult[];
  processing_time_ms: number;
  summary: string;
  critical_findings: string[];
}

export interface AuditRequest {
  declaration_data: Record<string, any>;
  documents?: Record<string, any>[];
  supporting_files?: string[];
}

export interface AuditHistoryResponse {
  total: number;
  skip: number;
  limit: number;
  results: Array<{
    id: string;
    overall_risk_score: number;
    overall_status: string;
    area_results: AreaResult[];
    processing_time_ms: number;
    created_at: string;
  }>;
}

class CustomsAuditService {
  async runFullAudit(request: AuditRequest): Promise<AuditReport> {
    try {
      const response = await api.post('/api/v1/customs-audit/full', request);
      return response.data;
    } catch (error) {
      console.error('Full customs audit failed:', error);
      throw error;
    }
  }

  async runAreaAudit(areaName: string, request: AuditRequest): Promise<AreaResult> {
    try {
      const response = await api.post(`/api/v1/customs-audit/area/${areaName}`, request);
      return response.data;
    } catch (error) {
      console.error(`Area audit '${areaName}' failed:`, error);
      throw error;
    }
  }

  async getReport(auditId: string): Promise<any> {
    try {
      const response = await api.get(`/api/v1/customs-audit/report/${auditId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get audit report:', error);
      throw error;
    }
  }

  async getHistory(skip: number = 0, limit: number = 20): Promise<AuditHistoryResponse> {
    try {
      const response = await api.get('/api/v1/customs-audit/history', {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get audit history:', error);
      throw error;
    }
  }

  async suggestFixes(declarationData: Record<string, any>, findings: Finding[]): Promise<Finding[]> {
    try {
      const response = await api.post('/api/v1/customs-audit/suggest-fixes', {
        declaration_data: declarationData,
        findings,
      });
      return response.data;
    } catch (error) {
      console.error('Suggest fixes failed:', error);
      throw error;
    }
  }

  async getHistoryByLrn(lrn: string, limit = 5): Promise<any[]> {
    try {
      const { data } = await api.get(`/api/v1/customs-audit/history-by-lrn?lrn=${encodeURIComponent(lrn)}&limit=${limit}`);
      return data.audits || [];
    } catch {
      return [];
    }
  }

  async downloadPdf(auditId: string): Promise<void> {
    try {
      const response = await api.get(`/api/v1/customs-audit/report/${auditId}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_report_${auditId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed:', error);
      throw error;
    }
  }
}

export const customsAuditService = new CustomsAuditService();
export default customsAuditService;
